/**
 * "Match with LinkedIn" on a company list (epic 2026-09-25-chopbook-company-lists,
 * 00 D4). PitchBook names each company's LinkedIn page, but a company only gets
 * its LinkedIn record (logo, people we know there) once ChopLink has read that
 * page. This asks ChopLink, when it is installed in this browser, to read the
 * pages of the list's companies it has never visited, one at a time, and then
 * joins each LinkedIn record with its PitchBook record.
 *
 * Paced for the recruiter's own LinkedIn session: at most `RUN_SIZE`
 * companies per click, a pause between pages, and ChopLink itself runs one
 * lookup at a time. A company that did not match is remembered in this
 * browser so the next click moves on to the rest of the list: a lasting answer
 * (not on LinkedIn, sent elsewhere, refused by the server) for a week, a
 * failure ChopLink says will not pass by retrying (no company id on the page)
 * for a day, any other failure (a timeout, "busy") for an hour. An expired
 * session stops the run: every next save would be refused too.
 * The list is read by name, a page of 500 at a time, until a run's worth of
 * untried companies is found. A page LinkedIn sends elsewhere (a renamed or
 * an acquired company) is counted as "went to another page" and never joined.
 *
 * Shown only while the list has such companies. Without ChopLink the button
 * stays, disabled, with the reason.
 */
import { Button, ProgressBar } from "@nu-assist/ui";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Square } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import type { LinkedinUnmatchedCompany } from "@nu-assist/contracts";

import { CompanyListsApiError, type CompanyListsClient } from "../../clients/companyLists.client";
import { type ChopLink, createChopLink } from "./chopLink";
import { COMPANY_LISTS_KEY } from "./queries";

/** Companies per click; the rest wait for the next one. */
export const RUN_SIZE = 50;
/** Pause between two LinkedIn pages. */
const PAUSE_MS = 3_000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** How long an answer that will not change soon keeps a company out of runs. */
const REMEMBER_LASTING_MS = 7 * DAY_MS;
/** How long a failure ChopLink calls not retryable keeps it out. */
const REMEMBER_FAILED_MS = DAY_MS;
/** How long a retryable failure (a timeout, ChopLink busy) keeps it out, so a
 * company that keeps failing does not take a slot at the front of every run. */
const REMEMBER_RETRYABLE_MS = 60 * 60 * 1000;
/** The most the unmatched endpoint returns per page. */
const FETCH_LIMIT = 500;
/** Pages read to fill one run (10,000 companies), so a huge list stays cheap. */
const MAX_PAGES = 20;

/** company id → when it may be tried again (ms since epoch). */
type Tried = Record<string, number>;

function triedKey(listId: string): string {
  return `nu-assist:linkedin-match-tried:${listId}`;
}

/** Companies tried recently without a match, still inside their wait. Browser
 * storage can be absent or throw (private windows); then nothing is
 * remembered. Entries written before 2026-10-01 held the try time, which is in
 * the past, so they read as expired. */
function readTried(listId: string, now: number): Tried {
  try {
    const raw = JSON.parse(window.localStorage.getItem(triedKey(listId)) ?? "{}") as Tried;
    return Object.fromEntries(
      Object.entries(raw).filter(([, until]) => typeof until === "number" && until > now)
    );
  } catch {
    return {};
  }
}

function writeTried(listId: string, tried: Tried): void {
  try {
    window.localStorage.setItem(triedKey(listId), JSON.stringify(tried));
  } catch {
    // Not remembered; the next click may try these again.
  }
}

interface MatchWithLinkedinProps {
  client: CompanyListsClient;
  listId: string;
  chopLink?: ChopLink;
  /** Injected in tests. */
  pause?: (ms: number) => Promise<void>;
}

const realPause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type Availability = "checking" | "absent" | "no-permission" | "ready";

interface Tally {
  matched: number;
  redirected: number;
  notFound: number;
  failed: number;
}

function summary(t: Tally, stopped: boolean): string {
  const parts = [`${t.matched} matched`];
  if (t.redirected) parts.push(`${t.redirected} went to another page`);
  if (t.notFound) parts.push(`${t.notFound} not on LinkedIn`);
  if (t.failed) parts.push(`${t.failed} failed`);
  return `${stopped ? "Stopped. " : ""}${parts.join(" · ")}.`;
}

export function MatchWithLinkedin({
  client,
  listId,
  chopLink,
  pause = realPause,
}: MatchWithLinkedinProps) {
  const queryClient = useQueryClient();
  const [link] = useState(() => chopLink ?? createChopLink());
  const [availability, setAvailability] = useState<Availability>("checking");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const stopRef = useRef(false);

  // Re-read after every run (the run records what it tried).
  const [triedVersion, setTriedVersion] = useState(0);
  const unmatched = useQuery({
    queryKey: [...COMPANY_LISTS_KEY, "linkedin-unmatched", listId, triedVersion],
    queryFn: async () => {
      const tried = readTried(listId, Date.now());
      const untried: LinkedinUnmatchedCompany[] = [];
      let skipped = 0;
      let total = 0;
      let offset = 0;
      let pagesRead = 0;
      for (let page = 0; page < MAX_PAGES; page++) {
        pagesRead += 1;
        const res = await client.linkedinUnmatched(listId, FETCH_LIMIT, offset);
        total = res.total;
        for (const c of res.items) {
          if (c.company_id in tried) skipped += 1;
          else untried.push(c);
        }
        offset += res.items.length;
        if (untried.length >= RUN_SIZE || res.items.length < FETCH_LIMIT || offset >= total) break;
      }
      // `capped`: every page read was tried recently, and pages were left
      // unread (the 10,000-company read cap); the hint must not say "all".
      const capped = untried.length === 0 && pagesRead === MAX_PAGES && offset < total;
      return { untried: untried.slice(0, RUN_SIZE), total, skipped, capped };
    },
    // A run bumps `triedVersion`; keep showing the last answer meanwhile so
    // the button does not vanish between runs.
    placeholderData: keepPreviousData,
  });

  // Leaving the page stops the run after the company in progress.
  useEffect(
    () => () => {
      stopRef.current = true;
    },
    []
  );

  useEffect(() => {
    let live = true;
    void link.ping().then((res) => {
      if (!live) return;
      setAvailability(!res ? "absent" : res.lookupPermission ? "ready" : "no-permission");
    });
    return () => {
      live = false;
    };
  }, [link]);

  const total = unmatched.data?.total ?? 0;
  if (!unmatched.data || total === 0) return null;
  const { untried, skipped, capped } = unmatched.data;

  async function run() {
    const items = untried;
    const triedNow: Tried = { ...readTried(listId, Date.now()) };
    const tally: Tally = { matched: 0, redirected: 0, notFound: 0, failed: 0 };
    let sessionExpired = false;
    stopRef.current = false;
    setProgress({ done: 0, total: items.length });
    for (const [i, company] of items.entries()) {
      if (i > 0) await pause(PAUSE_MS);
      if (stopRef.current) break;
      const res = await link.matchCompany(company.linkedin_slug);
      // How long this answer keeps the company out of the next runs (0 = it
      // is tried again on the next click).
      let rememberMs = 0;
      if (res.status === "matched") {
        try {
          await client.matchLinkedin(company.company_id, res.linkedinId);
          tally.matched += 1;
        } catch (err) {
          const status = err instanceof CompanyListsApiError ? err.status : 0;
          // 404: ChopLink's save already merged the two records and the
          // LinkedIn one survived (it was older), so this id is gone.
          if (status === 404) tally.matched += 1;
          else tally.failed += 1;
          // 409: an ambiguous slug, an id already set; it will not change soon.
          if (status === 409) rememberMs = REMEMBER_LASTING_MS;
        }
      } else if (res.status === "redirected") {
        tally.redirected += 1;
        rememberMs = REMEMBER_LASTING_MS;
      } else if (res.status === "not_found") {
        tally.notFound += 1;
        rememberMs = REMEMBER_LASTING_MS;
      } else {
        tally.failed += 1;
        if (res.status === "failed" && res.sessionExpired) {
          // Every next save would be refused too: stop instead of reading
          // the rest of the run's LinkedIn pages for nothing.
          sessionExpired = true;
        } else if (res.status === "failed") {
          // Older ChopLink builds send no `retryable`: treat it as retryable.
          rememberMs = res.retryable === false ? REMEMBER_FAILED_MS : REMEMBER_RETRYABLE_MS;
        }
      }
      if (rememberMs > 0) triedNow[company.company_id] = Date.now() + rememberMs;
      setProgress({ done: i + 1, total: items.length });
      if (sessionExpired) break;
    }
    writeTried(listId, triedNow);
    setTriedVersion((v) => v + 1);
    setProgress(null);
    void queryClient.invalidateQueries({ queryKey: COMPANY_LISTS_KEY });
    if (sessionExpired) {
      toast.error(
        `${summary(tally, false)} Then ChopLink’s nu-assist session expired: sign in again in ChopLink, then match the rest.`
      );
      return;
    }
    const message = summary(tally, stopRef.current);
    if (tally.failed) toast.warning(message);
    else toast.success(message);
  }

  if (progress) {
    return (
      <div className="flex items-center gap-2" data-testid="linkedin-match-progress">
        {/* Determinate: the run knows its size and counts each company. A
            fixed width, because the bar fills its parent and this row sits in
            the page header beside other buttons. */}
        <div className="w-24 shrink-0">
          <ProgressBar
            value={progress.done}
            max={progress.total}
            label="Matching with LinkedIn"
            live
          />
        </div>
        <span className="text-label text-text-secondary">
          Matching {Math.min(progress.done + 1, progress.total)} of {progress.total}…
        </span>
        <Button
          variant="secondary"
          onClick={() => {
            stopRef.current = true;
          }}
          data-testid="linkedin-match-stop"
        >
          <Square className="h-4 w-4" aria-hidden="true" />
          Stop
        </Button>
      </div>
    );
  }

  const hint =
    availability === "absent"
      ? "Needs ChopLink in this browser."
      : availability === "no-permission"
        ? "ChopLink can’t open LinkedIn company pages."
        : untried.length === 0
          ? capped
            ? "The first 10,000 companies (by name) were all tried recently without a match."
            : `All ${total.toLocaleString()} were tried recently without a match.`
          : skipped > 0
            ? `${skipped.toLocaleString()} tried recently without a match are skipped.`
            : null;
  // Companies past the pages read are counted as untried.
  const count = untried.length === 0 ? 0 : total - skipped;

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secondary"
        onClick={() => void run()}
        // While the list reloads after a run (or after a move to another list)
        // the shown data is the previous answer: the 50 just tried, or another
        // list's companies. A click then would read those pages again.
        disabled={availability !== "ready" || count === 0 || unmatched.isPlaceholderData}
        title={`Read the LinkedIn pages PitchBook names, with ChopLink: up to ${RUN_SIZE} companies per click`}
        data-testid="linkedin-match"
      >
        <Link2 className="h-4 w-4" aria-hidden="true" />
        {count > RUN_SIZE
          ? `Match ${RUN_SIZE} of ${count.toLocaleString()} with LinkedIn`
          : `Match ${count.toLocaleString()} with LinkedIn`}
      </Button>
      {hint && (
        <span className="text-caption text-text-muted" data-testid="linkedin-match-hint">
          {hint}
        </span>
      )}
    </div>
  );
}
